import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Typography, Paper, Chip, CircularProgress, IconButton, Tooltip,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  Dialog, DialogTitle, DialogContent, DialogActions, Button, LinearProgress,
  Checkbox, TextField, FormControlLabel, Alert, Snackbar,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import PersonIcon from '@mui/icons-material/Person';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import BuildCircleIcon from '@mui/icons-material/BuildCircle';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import { dashboardService } from '../services/api';
import { orderRefFromRow } from '../utils/orderLabel';

/** Оцінка випуску за цикл×гнізда від launched_at — як на екрані оператора (без списання). */
function useRealtimeCount(order) {
  const [count, setCount] = useState(null);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!order || order.status !== 'in_progress' || !order.launched_at) {
      setCount(null);
      return undefined;
    }
    const cycleSec = Number(order.realtime?.cycle_time_seconds);
    const cavities = Number(order.realtime?.cavities);
    if (!cycleSec || !cavities || cycleSec <= 0 || cavities <= 0) {
      setCount(null);
      return undefined;
    }
    const launchedMs = new Date(order.launched_at).getTime();
    if (Number.isNaN(launchedMs)) {
      setCount(null);
      return undefined;
    }
    const calc = () => Math.floor((Date.now() - launchedMs) / 1000 / cycleSec) * cavities;
    setCount(calc());
    timerRef.current = setInterval(() => setCount(calc()), 8000);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [
    order?.id,
    order?.status,
    order?.launched_at,
    order?.realtime?.cycle_time_seconds,
    order?.realtime?.cavities,
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  return count;
}

function RealtimeCountLine({ order, dense }) {
  const count = useRealtimeCount(order);
  if (count == null) return null;
  return (
    <Typography
      variant="caption"
      sx={{
        color: '#1b5e20',
        fontWeight: 700,
        display: 'block',
        mt: dense ? 0.35 : 0.5,
      }}
    >
      ● Реал-тайм: {Number(count).toLocaleString('uk-UA')} шт
    </Typography>
  );
}

const STATUS_STYLE = {
  green: { bg: '#e8f5e9', border: '#2e7d32', accent: '#1b5e20', chip: 'success' },
  yellow: { bg: '#fff8e1', border: '#f9a825', accent: '#f57f17', chip: 'warning' },
  red: { bg: '#ffebee', border: '#c62828', accent: '#b71c1c', chip: 'error' },
  gray: { bg: '#f5f5f5', border: '#9e9e9e', accent: '#616161', chip: 'default' },
};

const STATUS_ICON = {
  running: CheckCircleOutlineIcon,
  setup: BuildCircleIcon,
  pause: PauseCircleOutlineIcon,
  stop: WarningAmberIcon,
  idle: PowerSettingsNewIcon,
};

function fmtTime(v) {
  if (!v) return '—';
  try {
    return new Date(v).toLocaleString('uk-UA', {
      day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return '—';
  }
}

function toLocalInputValue(d) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function SummaryPill({ label, value, color }) {
  const s = STATUS_STYLE[color] || STATUS_STYLE.gray;
  return (
    <Box
      sx={{
        px: 2,
        py: 1.25,
        borderRadius: 2,
        bgcolor: s.bg,
        border: `1px solid ${s.border}`,
        minWidth: 110,
      }}
    >
      <Typography variant="caption" sx={{ color: s.accent, fontWeight: 600, display: 'block' }}>
        {label}
      </Typography>
      <Typography variant="h5" sx={{ fontWeight: 800, color: s.accent, lineHeight: 1.1 }}>
        {value}
      </Typography>
    </Box>
  );
}

function fmtMin(m) {
  if (m == null || Number.isNaN(Number(m))) return '—';
  const n = Number(m);
  if (n < 60) return `${Math.round(n)} хв`;
  const h = Math.floor(n / 60);
  const mm = Math.round(n % 60);
  return `${h} год ${mm} хв`;
}

/** Прогрес: здано (звіт). Теорію з лічильника тимчасово приховано. */
function OrderProgressBlock({ progress, accent, dense }) {
  if (!progress) return null;
  const pct = progress.percent != null ? Math.min(100, Math.max(0, Number(progress.percent))) : null;

  return (
    <Box sx={{ mt: dense ? 0.5 : 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.35, gap: 1 }}>
        <Typography variant="caption" color="text.secondary">Здано (звіт)</Typography>
        <Typography variant="caption" sx={{ fontWeight: 700 }}>
          {progress.fact_qty}/{progress.plan_qty}
          {pct != null ? ` · ${pct}%` : ''}
        </Typography>
      </Box>
      {pct != null && (
        <LinearProgress
          variant="determinate"
          value={pct}
          sx={{
            height: dense ? 6 : 8,
            borderRadius: 4,
            bgcolor: 'rgba(0,0,0,0.08)',
            '& .MuiLinearProgress-bar': { bgcolor: accent || '#2e7d32', borderRadius: 4 },
          }}
        />
      )}
    </Box>
  );
}

function MachineCard({ card, onOpen, selected, onToggleSelect, onEndBreak, endingBreakId }) {
  const s = STATUS_STYLE[card.status_color] || STATUS_STYLE.gray;
  const Icon = STATUS_ICON[card.status] || PrecisionManufacturingIcon;
  const progress = card.order?.progress;
  const canEndBreak = Boolean(card.break_id && (card.break_active || card.break_upcoming));

  return (
    <Paper
      elevation={0}
      onClick={() => onOpen?.(card)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onOpen?.(card); }}
      sx={{
        p: 2,
        height: '100%',
        borderRadius: 2.5,
        bgcolor: s.bg,
        border: `2px solid ${selected ? '#1565c0' : s.border}`,
        boxShadow: selected ? '0 0 0 2px rgba(21,101,192,0.25)' : undefined,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        cursor: 'pointer',
        transition: 'transform 0.15s ease, box-shadow 0.15s ease',
        '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 8px 24px rgba(0,0,0,0.08)' },
        position: 'relative',
      }}
    >
      <Checkbox
        size="small"
        checked={Boolean(selected)}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => {
          e.stopPropagation();
          onToggleSelect?.(card.machine_id, e.target.checked);
        }}
        sx={{
          position: 'absolute',
          top: 4,
          right: 4,
          p: 0.5,
          bgcolor: 'rgba(255,255,255,0.85)',
          borderRadius: 1,
          zIndex: 1,
        }}
        inputProps={{ 'aria-label': `Вибрати ${card.identifier || card.name}` }}
      />

      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, pr: 3.5 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 800, letterSpacing: 0.2, lineHeight: 1.15 }}>
            {card.identifier || card.name || `ТПА #${card.machine_id}`}
          </Typography>
          {card.name && card.identifier && card.name !== card.identifier && (
            <Typography variant="caption" color="text.secondary">{card.name}</Typography>
          )}
        </Box>
        <Icon sx={{ color: s.accent, fontSize: 28 }} />
      </Box>

      <Chip
        size="small"
        color={s.chip}
        label={card.status_label}
        sx={{ alignSelf: 'flex-start', fontWeight: 700 }}
      />

      {card.pause_reason_label ? (
        <Typography variant="caption" sx={{ color: s.accent, fontWeight: 600 }}>
          {card.pause_reason_label}
        </Typography>
      ) : null}

      {canEndBreak && (
        <Button
          size="small"
          variant="outlined"
          color="warning"
          disabled={endingBreakId === card.break_id}
          onClick={(e) => {
            e.stopPropagation();
            onEndBreak?.(card);
          }}
          sx={{ alignSelf: 'flex-start', fontWeight: 700, textTransform: 'none' }}
        >
          {endingBreakId === card.break_id
            ? '…'
            : (card.break_upcoming ? 'Скасувати перерву' : 'Завершити перерву')}
        </Button>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5 }}>
        <PersonIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {card.operator?.name || 'Не призначено'}
        </Typography>
      </Box>
      {card.operator?.source === 'assigned' && (
        <Typography variant="caption" color="text.secondary" sx={{ ml: 3.2, mt: -0.5 }}>
          призначено на зміну
        </Typography>
      )}

      <Box sx={{ mt: 'auto', pt: 1 }}>
        {card.order ? (
          <>
            <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap title={card.order.product_name}>
              #{card.order.id} · {card.order.product_name}
            </Typography>
            <OrderProgressBlock progress={progress} accent={s.accent} dense />
            <RealtimeCountLine order={card.order} dense />
          </>
        ) : (
          <Typography variant="body2" color="text.secondary">Немає активного замовлення</Typography>
        )}
      </Box>
    </Paper>
  );
}

function MachineDetailDialog({ open, machineId, onClose, navigate }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (!open || !machineId) return undefined;
    let cancelled = false;
    setLoading(true);
    setErr('');
    setDetail(null);
    dashboardService.shiftFloorMachine(machineId, { days: 7 })
      .then((res) => { if (!cancelled) setDetail(res.data); })
      .catch((e) => {
        if (!cancelled) setErr(e.response?.data?.detail || e.message || 'Помилка');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, machineId]);

  const s = STATUS_STYLE[detail?.status_color] || STATUS_STYLE.gray;
  const co = detail?.current_order;
  const progress = co?.progress;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            {detail?.machine?.identifier || detail?.machine?.name || (machineId ? `ТПА #${machineId}` : 'ТПА')}
          </Typography>
          {detail?.machine?.name && detail?.machine?.identifier !== detail?.machine?.name && (
            <Typography variant="caption" color="text.secondary">{detail.machine.name}</Typography>
          )}
        </Box>
        {detail && (
          <Chip size="small" color={s.chip} label={detail.status_label} sx={{ fontWeight: 700 }} />
        )}
      </DialogTitle>
      <DialogContent dividers>
        {loading && <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress /></Box>}
        {err && <Typography color="error">{err}</Typography>}
        {detail && !loading && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <Paper variant="outlined" sx={{ p: 2, bgcolor: s.bg, borderColor: s.border }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>Зараз</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Оператор</Typography>
                  <Typography fontWeight={700}>{detail.operator?.name || 'Не призначено'}</Typography>
                  {detail.operator?.shift_name && (
                    <Typography variant="caption" color="text.secondary">{detail.operator.shift_name}</Typography>
                  )}
                </Box>
                <Box sx={{ flex: 1, minWidth: 200 }}>
                  <Typography variant="caption" color="text.secondary">Замовлення</Typography>
                  {co ? (
                    <>
                      <Typography fontWeight={700}>#{co.id} · {co.product_name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        У роботі: {fmtMin(co.elapsed_minutes)}
                        {co.launched_at ? ` · старт ${fmtTime(co.launched_at)}` : ''}
                      </Typography>
                      <OrderProgressBlock progress={progress} accent={s.accent} />
                      <RealtimeCountLine order={co} />
                    </>
                  ) : (
                    <Typography color="text.secondary">Немає активного замовлення</Typography>
                  )}
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">За {detail.period_days} дн.</Typography>
                  <Typography fontWeight={700}>{detail.totals?.produced_qty ?? 0} шт</Typography>
                  <Typography variant="caption" color="text.secondary">
                    брак {detail.totals?.defect_qty ?? 0}
                  </Typography>
                </Box>
              </Box>
            </Paper>

            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
                Хто працював (сесії)
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Оператор</TableCell>
                      <TableCell>Початок</TableCell>
                      <TableCell>Кінець</TableCell>
                      <TableCell align="right">Тривалість</TableCell>
                      <TableCell align="right">Показник</TableCell>
                      <TableCell align="right">Δ / теорія</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(detail.operator_sessions || []).length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ color: 'text.secondary' }}>
                          Немає передач зміни за період
                        </TableCell>
                      </TableRow>
                    ) : detail.operator_sessions.map((sess, idx) => (
                      <TableRow key={sess.end_handover_id || sess.start_handover_id || idx}>
                        <TableCell>
                          {sess.operator_name || '—'}
                          {sess.open ? <Chip size="small" label="на зміні" color="success" sx={{ ml: 1 }} /> : null}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtTime(sess.started_at)}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtTime(sess.ended_at)}</TableCell>
                        <TableCell align="right">{fmtMin(sess.duration_minutes)}</TableCell>
                        <TableCell align="right">{sess.counter_reading ?? '—'}</TableCell>
                        <TableCell align="right">
                          {sess.cycles_delta != null
                            ? `${sess.cycles_delta}${sess.theory_qty != null ? ` → ${sess.theory_qty} шт` : ''}`
                            : (sess.counter_reading != null ? 'немає бази' : '—')}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>Звіти за зміну</Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Час</TableCell>
                      <TableCell>Оператор</TableCell>
                      <TableCell>Замовлення</TableCell>
                      <TableCell align="right">Випуск</TableCell>
                      <TableCell align="right">Брак</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(detail.reports || []).length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ color: 'text.secondary' }}>Немає звітів</TableCell>
                      </TableRow>
                    ) : detail.reports.map((r) => (
                      <TableRow key={r.id} hover>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtTime(r.created_at)}</TableCell>
                        <TableCell>{r.operator_name || '—'}</TableCell>
                        <TableCell>
                          {r.order_id ? (r.order_ref_display || orderRefFromRow(r)) : '—'}
                          {r.product_name ? ` · ${r.product_name}` : ''}
                        </TableCell>
                        <TableCell align="right">{r.produced_qty ?? '—'}</TableCell>
                        <TableCell align="right">{r.defect_qty ?? '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>Історія замовлень</Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 280 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>ID</TableCell>
                      <TableCell>Виріб</TableCell>
                      <TableCell>Статус</TableCell>
                      <TableCell align="right">Факт / план</TableCell>
                      <TableCell align="right">Час</TableCell>
                      <TableCell>Оператор</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(detail.orders || []).map((o) => (
                      <TableRow
                        key={o.id}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => { onClose?.(); navigate?.(`/planner-v2?order=${o.id}`); }}
                      >
                        <TableCell>#{o.id}</TableCell>
                        <TableCell>{o.product_name}</TableCell>
                        <TableCell>{o.status_display || o.status}</TableCell>
                        <TableCell align="right">{o.fact_qty}/{o.plan_qty}</TableCell>
                        <TableCell align="right">{fmtMin(o.duration_minutes)}</TableCell>
                        <TableCell>{o.operator_name || '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>

            {(detail.breaks || []).length > 0 && (
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>Простої</Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>З</TableCell>
                        <TableCell>По</TableCell>
                        <TableCell align="right">Тривалість</TableCell>
                        <TableCell>Причина</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {detail.breaks.map((b) => (
                        <TableRow key={b.id}>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtTime(b.start_at)}</TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtTime(b.end_at)}</TableCell>
                          <TableCell align="right">{fmtMin(b.duration_minutes)}</TableCell>
                          <TableCell>{b.reason || '—'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {(detail.logs || []).length > 0 && (
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>Події</Typography>
                <Box sx={{ maxHeight: 200, overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1 }}>
                  {detail.logs.map((lg) => (
                    <Box key={lg.id} sx={{ py: 0.75, borderBottom: '1px solid', borderColor: 'divider' }}>
                      <Typography variant="caption" color="text.secondary">{fmtTime(lg.created_at)} · {lg.order_ref_display || orderRefFromRow(lg)}</Typography>
                      <Typography variant="body2">{lg.action_label || lg.action}</Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Закрити</Button>
        {co?.id && (
          <Button
            variant="contained"
            onClick={() => { onClose?.(); navigate?.(`/planner-v2?order=${co.id}`); }}
          >
            Відкрити замовлення
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

function ScheduleBreakDialog({ open, machines, onClose, onSuccess }) {
  const defaults = useMemo(() => {
    // Старт «зараз» (округлення до хвилини), щоб перерва одразу ставила ТПА на паузу
    const start = new Date();
    start.setSeconds(0, 0);
    const end = new Date(start.getTime() + 30 * 60 * 1000);
    return { start: toLocalInputValue(start), end: toLocalInputValue(end) };
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const [startAt, setStartAt] = useState(defaults.start);
  const [endAt, setEndAt] = useState(defaults.end);
  const [reason, setReason] = useState('Планова перерва / обід');
  const [pauseRunning, setPauseRunning] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (!open) return;
    setStartAt(defaults.start);
    setEndAt(defaults.end);
    setReason('Планова перерва / обід');
    setPauseRunning(true);
    setErr('');
  }, [open, defaults]);

  const handleSubmit = async () => {
    setErr('');
    if (!startAt || !endAt) {
      setErr('Вкажіть початок і кінець перерви');
      return;
    }
    const start = new Date(startAt);
    const end = new Date(endAt);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
      setErr('Некоректний формат дати/часу');
      return;
    }
    if (end <= start) {
      setErr('Кінець перерви має бути пізніше за початок');
      return;
    }
    setSaving(true);
    try {
      const res = await dashboardService.scheduleBreaks({
        machine_ids: machines.map((m) => m.machine_id),
        start_at: start.toISOString(),
        end_at: end.toISOString(),
        reason: reason.trim() || 'Планова перерва',
        pause_running: pauseRunning,
      });
      const created = res.data?.created || [];
      const skipped = res.data?.skipped || [];
      const errors = res.data?.errors || [];
      if (!created.length && !skipped.length) {
        setErr(
          errors.map((e) => `ТПА #${e.machine_id}: ${e.error}`).join('; ')
          || 'Не вдалося запланувати перерву',
        );
        return;
      }
      onSuccess?.(res.data);
      onClose?.();
    } catch (e) {
      const d = e.response?.data;
      setErr(
        d?.detail
        || d?.end_at
        || (typeof d === 'string' ? d : null)
        || e.message
        || 'Помилка збереження',
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 800 }}>
        Запланувати перерву ({machines.length})
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
            {machines.map((m) => (
              <Chip
                key={m.machine_id}
                size="small"
                label={m.identifier || m.name || `#${m.machine_id}`}
                sx={{ fontWeight: 700 }}
              />
            ))}
          </Box>
          <TextField
            label="Початок"
            type="datetime-local"
            value={startAt}
            onChange={(e) => setStartAt(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          <TextField
            label="Кінець"
            type="datetime-local"
            value={endAt}
            onChange={(e) => setEndAt(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          <TextField
            label="Причина / коментар"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            fullWidth
            multiline
            minRows={2}
          />
          <FormControlLabel
            control={(
              <Checkbox
                checked={pauseRunning}
                onChange={(e) => setPauseRunning(e.target.checked)}
              />
            )}
            label="Поставити на паузу активні замовлення, якщо перерва вже триває"
          />
          {err && <Alert severity="error">{err}</Alert>}
          <Typography variant="caption" color="text.secondary">
            Оператори вибраних ТПА отримають сповіщення з часом перерви.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Скасувати</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={saving || machines.length === 0}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <EventBusyIcon />}
        >
          Запланувати
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function ShiftFloorDashboardPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [detail, setDetail] = useState(null);
  const [machineId, setMachineId] = useState(null);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [breakOpen, setBreakOpen] = useState(false);
  const [endingBreakId, setEndingBreakId] = useState(null);
  const [snack, setSnack] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await dashboardService.shiftFloor({ reports_limit: 50 });
      setData(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Помилка завантаження');
    } finally {
      setLoading(false);
    }
  }, []);

  // Deep-link зі сповіщень: /shift-floor?machine=14
  useEffect(() => {
    const raw = searchParams.get('machine');
    if (!raw) return undefined;
    const id = Number(raw);
    if (!Number.isFinite(id) || id <= 0) return undefined;
    setMachineId(id);
    return undefined;
  }, [searchParams]);

  const handleEndBreak = useCallback(async (card) => {
    if (!card?.break_id) return;
    const label = card.break_upcoming ? 'Скасувати заплановану перерву?' : 'Завершити перерву зараз і зняти паузу?';
    if (!window.confirm(label)) return;
    setEndingBreakId(card.break_id);
    try {
      const res = await dashboardService.endBreak(card.break_id, { resume: true });
      const n = res.data?.resumed?.length || 0;
      setSnack(
        res.data?.action === 'deleted'
          ? `Перерву скасовано${n ? `, знято паузу з ${n} ЗВ` : ''}`
          : `Перерву завершено${n ? `, відновлено ${n} ЗВ` : ''}`,
      );
      load();
    } catch (e) {
      setSnack(e.response?.data?.detail || e.message || 'Не вдалося завершити перерву');
    } finally {
      setEndingBreakId(null);
    }
  }, [load]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const t = setInterval(() => { load(); }, 30000);
    return () => clearInterval(t);
  }, [load]);

  const summary = data?.summary;
  const machines = data?.machines || [];
  const reports = data?.reports || [];

  const sortedMachines = useMemo(() => {
    const rank = { stop: 0, pause: 1, setup: 2, running: 3, idle: 4 };
    return [...machines].sort((a, b) => {
      const ra = rank[a.status] ?? 9;
      const rb = rank[b.status] ?? 9;
      if (ra !== rb) return ra - rb;
      return String(a.identifier || '').localeCompare(String(b.identifier || ''), 'uk');
    });
  }, [machines]);

  const selectedMachines = useMemo(
    () => sortedMachines.filter((m) => selectedIds.has(m.machine_id)),
    [sortedMachines, selectedIds],
  );

  const allSelected = sortedMachines.length > 0
    && sortedMachines.every((m) => selectedIds.has(m.machine_id));

  const toggleSelect = useCallback((id, checked) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(sortedMachines.map((m) => m.machine_id)));
    }
  }, [allSelected, sortedMachines]);

  return (
    <Box
      sx={{
        minHeight: '100%',
        background: 'linear-gradient(165deg, #f0f4f8 0%, #e8eef5 40%, #f7f5f0 100%)',
        mx: { xs: -2, sm: -3 },
        px: { xs: 2, sm: 3 },
        py: 2.5,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 2.5 }}>
        <Box>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 850,
              letterSpacing: '-0.02em',
              fontFamily: '"Segoe UI", "IBM Plex Sans", system-ui, sans-serif',
            }}
          >
            Цех · моніторинг зміни
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {data?.shift?.name
              ? `Зміна: ${data.shift.name}`
              : 'Поточний стан ТПА та звіти операторів'}
            {data?.generated_at ? ` · оновлено ${fmtTime(data.generated_at)}` : ''}
          </Typography>
        </Box>
        <Tooltip title="Оновити">
          <IconButton onClick={load} disabled={loading} sx={{ bgcolor: 'white', border: '1px solid #d0d7de' }}>
            {loading ? <CircularProgress size={20} /> : <RefreshIcon />}
          </IconButton>
        </Tooltip>
      </Box>

      {error && (
        <Paper sx={{ p: 2, mb: 2, bgcolor: '#ffebee', color: '#b71c1c' }}>{error}</Paper>
      )}

      {summary && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25, mb: 3 }}>
          <SummaryPill label="В роботі" value={summary.running} color="green" />
          <SummaryPill label="Наладка" value={summary.setup} color="yellow" />
          <SummaryPill label="Пауза" value={summary.pause} color="yellow" />
          <SummaryPill label="Зупинка" value={summary.stop} color="red" />
          <SummaryPill label="Вільні" value={summary.idle} color="gray" />
          {summary.reports_with_discrepancy > 0 && (
            <SummaryPill label="Розбіжності" value={summary.reports_with_discrepancy} color="red" />
          )}
        </Box>
      )}

      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1.5,
          mb: 1.5,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 800 }}>
          Активні ТПА
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
          <FormControlLabel
            control={(
              <Checkbox
                size="small"
                checked={allSelected}
                indeterminate={selectedIds.size > 0 && !allSelected}
                onChange={toggleSelectAll}
                disabled={sortedMachines.length === 0}
              />
            )}
            label={<Typography variant="body2">Вибрати всі</Typography>}
            sx={{ mr: 0.5 }}
          />
          <Button
            variant="contained"
            color="warning"
            startIcon={<EventBusyIcon />}
            disabled={selectedIds.size === 0}
            onClick={() => setBreakOpen(true)}
            sx={{ fontWeight: 700 }}
          >
            Запланувати перерву
            {selectedIds.size > 0 ? ` (${selectedIds.size})` : ''}
          </Button>
          {selectedIds.size > 0 && (
            <Button size="small" onClick={() => setSelectedIds(new Set())}>
              Скинути
            </Button>
          )}
        </Box>
      </Box>

      {loading && !data ? (
        <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress /></Box>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(3, 1fr)',
              lg: 'repeat(4, 1fr)',
            },
            gap: 1.75,
            mb: 4,
          }}
        >
          {sortedMachines.map((m) => (
            <MachineCard
              key={m.machine_id}
              card={m}
              selected={selectedIds.has(m.machine_id)}
              onToggleSelect={toggleSelect}
              onOpen={(c) => setMachineId(c.machine_id)}
              onEndBreak={handleEndBreak}
              endingBreakId={endingBreakId}
            />
          ))}
          {sortedMachines.length === 0 && (
            <Typography color="text.secondary">Немає активних станків у довіднику</Typography>
          )}
        </Box>
      )}

      <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.5 }}>
        Звіти за зміну
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Останні подані звіти. Червоний індикатор — розбіжність лічильника (теорія vs здано).
      </Typography>

      <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 2, border: '1px solid #e0e4ea' }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell>Час</TableCell>
              <TableCell>ТПА</TableCell>
              <TableCell>Оператор</TableCell>
              <TableCell>Замовлення</TableCell>
              <TableCell align="right">Випуск</TableCell>
              <TableCell align="right">Брак</TableCell>
              <TableCell align="right">Δ лічильника</TableCell>
              <TableCell align="center">Розбіжність</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {reports.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ color: 'text.secondary', py: 3 }}>
                  Немає звітів за останні години
                </TableCell>
              </TableRow>
            ) : reports.map((r) => {
              const c = r.counter;
              const disc = c?.has_discrepancy;
              return (
                <TableRow
                  key={r.id}
                  hover
                  sx={{ cursor: 'pointer', bgcolor: disc ? 'rgba(198,40,40,0.04)' : undefined }}
                  onClick={() => setDetail(r)}
                >
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtTime(r.created_at)}</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>{r.machine_identifier || '—'}</TableCell>
                  <TableCell>{r.operator_name || '—'}</TableCell>
                  <TableCell>
                    {r.order_id ? (r.order_ref_display || orderRefFromRow(r)) : '—'}
                    {r.product_name ? ` · ${r.product_name}` : ''}
                  </TableCell>
                  <TableCell align="right">{r.produced_qty ?? '—'}</TableCell>
                  <TableCell align="right">{r.defect_qty ?? '—'}</TableCell>
                  <TableCell align="right">
                    {c?.cycles_delta != null ? c.cycles_delta : '—'}
                  </TableCell>
                  <TableCell align="center">
                    {disc ? (
                      <Chip
                        size="small"
                        color="error"
                        icon={<WarningAmberIcon />}
                        label={Number(c.unaccounted_loss).toFixed(0)}
                        sx={{ fontWeight: 700 }}
                      />
                    ) : c ? (
                      <Chip size="small" label="OK" color="success" variant="outlined" />
                    ) : (
                      '—'
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <MachineDetailDialog
        open={Boolean(machineId)}
        machineId={machineId}
        onClose={() => {
          setMachineId(null);
          if (searchParams.has('machine') || searchParams.has('order')) {
            const next = new URLSearchParams(searchParams);
            next.delete('machine');
            next.delete('order');
            setSearchParams(next, { replace: true });
          }
        }}
        navigate={navigate}
      />

      <ScheduleBreakDialog
        open={breakOpen}
        machines={selectedMachines}
        onClose={() => setBreakOpen(false)}
        onSuccess={(result) => {
          const n = result?.created?.length || 0;
          const skipN = result?.skipped?.length || 0;
          const errN = result?.errors?.length || 0;
          setSelectedIds(new Set());
          const parts = [];
          if (n) parts.push(`заплановано на ${n} ТПА`);
          if (skipN) parts.push(`вже були: ${skipN}`);
          if (errN) parts.push(`помилок: ${errN}`);
          setSnack(
            parts.length
              ? `Перерва: ${parts.join(', ')}.`
              : 'Перерву заплановано.',
          );
          load();
        }}
      />

      <Dialog open={Boolean(detail)} onClose={() => setDetail(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Звіт #{detail?.id}</DialogTitle>
        <DialogContent dividers>
          {detail && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography><strong>Час:</strong> {fmtTime(detail.created_at)}</Typography>
              <Typography><strong>ТПА:</strong> {detail.machine_identifier || '—'}</Typography>
              <Typography><strong>Оператор:</strong> {detail.operator_name || '—'}</Typography>
              <Typography>
                <strong>Замовлення:</strong>{' '}
                {detail.order_id
                  ? `${detail.order_ref_display || orderRefFromRow(detail)}${detail.product_name ? ` · ${detail.product_name}` : ''}`
                  : '—'}
              </Typography>
              <Typography><strong>Випуск:</strong> {detail.produced_qty} шт</Typography>
              <Typography><strong>Брак:</strong> {detail.defect_qty} шт</Typography>
              <Typography><strong>Відходи:</strong> {detail.waste_kg ?? 0} кг (невідн. {detail.non_recoverable_waste_kg ?? 0} кг)</Typography>
              {detail.comment ? <Typography><strong>Коментар:</strong> {detail.comment}</Typography> : null}
              {detail.counter && (
                <Box sx={{ mt: 1, p: 1.5, bgcolor: '#f5f7fa', borderRadius: 1.5 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5 }}>Лічильник</Typography>
                  <Typography variant="body2">Показник: {detail.counter.counter_reading}</Typography>
                  <Typography variant="body2">Попередній: {detail.counter.previous_counter_reading ?? '—'}</Typography>
                  <Typography variant="body2">Цикли (Δ): {detail.counter.cycles_delta ?? '—'}</Typography>
                  <Typography variant="body2">Гніздність: {detail.counter.cavities ?? '—'}</Typography>
                  <Typography variant="body2">Теорія: {detail.counter.theoretical_qty ?? '—'}</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: detail.counter.has_discrepancy ? '#c62828' : 'inherit' }}>
                    Необліковано: {detail.counter.unaccounted_loss != null ? Number(detail.counter.unaccounted_loss).toFixed(1) : '—'}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetail(null)}>Закрити</Button>
          {detail?.order_id && (
            <Button
              variant="contained"
              onClick={() => {
                setDetail(null);
                navigate(`/planner-v2?order=${detail.order_id}`);
              }}
            >
              Відкрити в Планері
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(snack)}
        autoHideDuration={5000}
        onClose={() => setSnack('')}
        message={snack}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
}
